import { useQuery } from '@tanstack/react-query';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useDelayedRefetch } from 'src/hooks/useDelayedRefetch';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import {
  EmptyEntityWithTicketsQuery,
  QueryWithViewMode,
} from 'src/utils/eventQueryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CatalogResults,
  EntityWithTicketsQuery,
  EventWithData,
  Feature,
  Listing,
  PurchaseOrderTicketGroup,
  Sale,
} from 'src/WebApiController';
import { useContext as useContextWithSelector } from 'use-context-selector';

import { useAppContext } from '../AppContext';
import {
  useCatalogDataContextallEventIdsSelector,
  useCatalogDataContextDataSelector,
} from '../CatalogDataContext';
import {
  useFilterQueryContextFilterQuerySelector,
  useFilterQueryContextIsQueryInitializedSelector,
} from '../FilterQueryContext/FilterQueryContext.selectors';
import {
  ISearchDataContext,
  SearchDataContext,
  SearchDataContextProviderProps,
  SearchDataContextWithSelector,
} from './SearchDataContext.types';
import {
  buildCatalogAndEventsFromListingsSearchData,
  buildCatalogAndEventsFromSalesSearchData,
  buildResolvedEventIds,
} from './SearchDataContext.utils';

/**
 * @deprecated use the explicit context selectors to avoid unnecessary re-render
 */
export const useSearchDataContext = () => {
  const hasContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const context = useContext(SearchDataContext);
  const contextWithSelector = useContextWithSelector(
    SearchDataContextWithSelector
  );
  return hasContextWithSelectorFeature ? contextWithSelector : context;
};

/**
 * Context provider to retrieve search data from the server, and provide a similar interface to the catalog data context when consumed by table components.
 *
 * The search data context requests search data from the server which contains catalog data and entity data. This is later transformed into catalog data and entity data.
 * The context optionally provides a fallback to the catalog data context when the catalog data from search data is not available.
 */
export function SearchDataContextProvider<
  TQuery extends EntityWithTicketsQuery & QueryWithViewMode,
>({
  queryKey,
  entityType,
  getSearchData,
  getSearchDataSummary,
  children,
  disabled,
  searchSubmitVersion = 0,
}: SearchDataContextProviderProps<TQuery>) {
  const hasContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const { showErrorDialog } = useClientLogContext();
  const [filterQuery] = useFilterQueryContextFilterQuerySelector<TQuery>();
  const isQueryInitialized = useFilterQueryContextIsQueryInitializedSelector();
  const { activeAccountWebClientConfig } = useAppContext();

  const catalogDataFallback = useCatalogDataContextDataSelector();
  const catalogAllEventIds = useCatalogDataContextallEventIdsSelector();

  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();

  const {
    searchText,
    viewMode, // this is not used for server and keeping it in this cause refresh to happen 2x
    ...serverSideFilterQuery // Don't use it because it's a new object on every render
  } = filterQuery;

  const searchDataFilterQuery = useMemo(() => {
    const query = Object.fromEntries(
      Object.entries(serverSideFilterQuery).filter(([_, v]) => v != null)
    );
    return {
      ...query,
      timezoneOffsetMins:
        query.timezoneOffsetMins ??
        EmptyEntityWithTicketsQuery.timezoneOffsetMins,
    } as TQuery;
  }, [serverSideFilterQuery]);

  const searchDataSummaryFilterQuery = useMemo(() => {
    return {
      ...searchDataFilterQuery,
      searchOptions: null,
    } as TQuery;
  }, [searchDataFilterQuery]);

  const resolvedEventIds = useMemo(() => {
    // When the user is filtering by direct IDs (purchaseOrderIds or eventIdsOverride),
    // return an empty array so that hydrateFiltersWithLegacyEventIds sets
    // eventOrMappingIds=[] and oldPosEventIds=[] on the API payload.
    // Without this, URL-persisted eventOrMappingIds from a previous session
    // leak through buildResolvedEventIds and narrow the query, e.g. a
    // "PO Not-In 9507675" also filters to a stale event set.
    const hasDirectIdFilter = Boolean(
      searchDataFilterQuery.purchaseOrderIds?.length ||
      searchDataFilterQuery.eventIdsOverride?.length
    );
    if (hasDirectIdFilter) {
      return [];
    }
    return buildResolvedEventIds(
      searchDataFilterQuery.eventOrMappingIds,
      catalogAllEventIds,
      catalogDataFallback?.events
    );
  }, [searchDataFilterQuery, catalogAllEventIds, catalogDataFallback]);

  const shouldQuerySearchData = useMemo(
    () =>
      activeAccountWebClientConfig.activeAccountId != null &&
      searchDataFilterQuery != null &&
      isQueryInitialized &&
      !disabled,
    [
      activeAccountWebClientConfig.activeAccountId,
      searchDataFilterQuery,
      isQueryInitialized,
      disabled,
    ]
  );

  const searchDataQueryKey = useMemo(
    () => [
      queryKey + 'Expanded',
      searchDataFilterQuery,
      resolvedEventIds,
      searchSubmitVersion,
    ],
    [queryKey, searchDataFilterQuery, resolvedEventIds, searchSubmitVersion]
  );

  const searchDataSummaryQueryKey = useMemo(
    () => [
      queryKey + 'Summary',
      searchDataSummaryFilterQuery,
      resolvedEventIds,
      searchSubmitVersion,
    ],
    [
      queryKey,
      searchDataSummaryFilterQuery,
      resolvedEventIds,
      searchSubmitVersion,
    ]
  );

  const searchDataQuery = useQuery({
    queryKey: [searchDataQueryKey],
    queryFn: async () => {
      if (!shouldQuerySearchData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const data = await getSearchData?.(
            resolvedEventIds,
            searchDataFilterQuery as TQuery
          );

          return data;
        },
        (error: ErrorTypes) => {
          showErrorDialog('getSearchData', error, {
            trackErrorData: {
              eventIds: resolvedEventIds,
              filterQuery,
            },
          });
        }
      );
    },
    enabled: shouldQuerySearchData,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false,
  });

  const searchDataSummaryQuery = useQuery({
    queryKey: [searchDataSummaryQueryKey],
    queryFn: async () => {
      if (!shouldQuerySearchData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const data = await getSearchDataSummary?.(
            resolvedEventIds,
            searchDataSummaryFilterQuery as TQuery
          );

          return data;
        },
        (error: ErrorTypes) => {
          showErrorDialog('getSearchDataSummary', error, {
            trackErrorData: {
              eventIds: resolvedEventIds,
              filterQuery,
            },
          });
        }
      );
    },
    enabled: shouldQuerySearchData,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false,
  });

  // Local overrides for items that have been updated in the UI before the next refetch
  // Key is the item ID, value is the partial item data to merge
  const [localOverrides, setLocalOverrides] = useState<
    Record<string, Partial<Listing | Sale | PurchaseOrderTicketGroup>>
  >({});

  // Clear local overrides when the query key changes (new filter/sort/pagination).
  // We intentionally do NOT clear on data changes (refetches with the same key),
  // because after a bulk edit the DB-fetched overrides must survive until the user
  // triggers a new search — OpenSearch may still have stale data for several seconds.
  // Use JSON.stringify for value-based comparison since searchDataQueryKey is a new
  // object reference on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchDataQueryKeyStr = JSON.stringify(searchDataQueryKey);
  useEffect(() => {
    setLocalOverrides({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDataQueryKeyStr]);

  // Handle query errors
  useEffect(() => {
    const failureReason =
      shouldQuerySearchData && searchDataQuery.failureReason;

    if (failureReason) {
      const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
        null,
        failureReason?.toString()
      );
      setErrorInfo({
        errorHeader: headerDisplay,
        errorMessage: messageDisplay,
      });
    } else if (errorInfo) {
      setErrorInfo(undefined);
    }
  }, [errorInfo, searchDataQuery.failureReason, shouldQuerySearchData]);

  // Update an item in the event data (used for optimistic UI updates)
  const updateItemInEvent = useCallback(
    (
      item: Listing | Sale | PurchaseOrderTicketGroup,
      entityType: ActionOutboxEntityType,
      _sectionalUpdate?: boolean
    ) => {
      // Determine the item ID based on entity type
      const itemId =
        entityType === ActionOutboxEntityType.Purchase
          ? (item as PurchaseOrderTicketGroup).tgId
          : item.id;

      if (!itemId) {
        return;
      }

      // Store the override for this item, filtering out null/undefined values
      // to prevent sectional API responses from overwriting existing data
      const nonNullFields = Object.fromEntries(
        Object.entries(item).filter(([, v]) => v != null)
      );
      setLocalOverrides((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          ...nonNullFields,
        },
      }));
    },
    []
  );

  const refetch = useCallback(async () => {
    // Clear local overrides so fresh search data isn't overwritten by stale values
    setLocalOverrides({});
    await Promise.all([
      searchDataQuery.refetch(),
      searchDataSummaryQuery.refetch(),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDataQuery.refetch, searchDataSummaryQuery.refetch]);

  const { delayedRefetch, isRefreshingAfterBulkEdit } =
    useDelayedRefetch(refetch);

  const { catalogResults, eventsTransformed } = useMemo(() => {
    if (searchDataQuery.data == null) {
      return {
        catalogResults: {} as CatalogResults,
        eventsTransformed: [] as EventWithData[],
      };
    }

    const applyOverridesToEvents = (
      events: EventWithData[]
    ): EventWithData[] => {
      if (Object.keys(localOverrides).length === 0) return events;

      return events.map((eventData) => {
        const newEventData = { ...eventData };

        if (
          entityType === ActionOutboxEntityType.Listing &&
          newEventData.entities.listings
        ) {
          newEventData.entities = {
            ...newEventData.entities,
            listings: newEventData.entities.listings.map((listing) => {
              const override = localOverrides[listing.id] as
                | Partial<Listing>
                | undefined;
              return override
                ? ({ ...listing, ...override } as Listing)
                : listing;
            }),
          };
        } else if (
          entityType === ActionOutboxEntityType.Sale &&
          newEventData.entities.sales
        ) {
          newEventData.entities = {
            ...newEventData.entities,
            sales: newEventData.entities.sales.map((sale) => {
              const override = localOverrides[sale.id] as
                | Partial<Sale>
                | undefined;
              return override ? ({ ...sale, ...override } as Sale) : sale;
            }),
          };
        }

        return newEventData;
      });
    };

    if (entityType === ActionOutboxEntityType.Sale) {
      const result = buildCatalogAndEventsFromSalesSearchData(
        searchDataQuery.data,
        searchDataSummaryQuery.data,
        catalogDataFallback
      );

      return {
        ...result,
        eventsTransformed: applyOverridesToEvents(result.eventsTransformed),
      };
    }

    if (entityType === ActionOutboxEntityType.Listing) {
      const result = buildCatalogAndEventsFromListingsSearchData(
        searchDataQuery.data,
        searchDataSummaryQuery.data,
        catalogDataFallback
      );

      return {
        ...result,
        eventsTransformed: applyOverridesToEvents(result.eventsTransformed),
      };
    }

    return {
      catalogResults: {} as CatalogResults,
      eventsTransformed: [] as EventWithData[],
    };
  }, [
    searchDataQuery.data,
    entityType,
    searchDataSummaryQuery.data,
    catalogDataFallback,
    localOverrides,
  ]);

  const contextValue = useMemo<ISearchDataContext>(
    () => ({
      isEnabled: !disabled,
      isLoading: searchDataQuery.isLoading,
      isFetching: searchDataQuery.isFetching,
      data: catalogResults ?? undefined,
      pitId: searchDataQuery.data?.pitId,
      totalItemsCount: searchDataQuery.data?.totalItemsCount,
      maxResultWindow: searchDataQuery.data?.maxResultWindow,
      searchAfter: searchDataQuery.data?.searchAfter,
      sortedItemIds: searchDataQuery.data?.sortedItemIds,
      errorInfo,
      eventsTransformed,
      refetch,
      delayedRefetch,
      isRefreshingAfterBulkEdit,
      updateItemInEvent,
    }),
    [
      catalogResults,
      delayedRefetch,
      disabled,
      errorInfo,
      eventsTransformed,
      isRefreshingAfterBulkEdit,
      refetch,
      searchDataQuery.data?.pitId,
      searchDataQuery.data?.searchAfter,
      searchDataQuery.data?.sortedItemIds,
      searchDataQuery.data?.totalItemsCount,
      searchDataQuery.isFetching,
      searchDataQuery.isLoading,
      updateItemInEvent,
    ]
  );

  if (hasContextWithSelectorFeature) {
    return (
      <SearchDataContextWithSelector.Provider value={contextValue}>
        {children}
      </SearchDataContextWithSelector.Provider>
    );
  }

  return (
    <SearchDataContext.Provider value={contextValue}>
      {children}
    </SearchDataContext.Provider>
  );
}
